import { takeCompletionReturn } from "../../utils/profileCompletionReturn";
import {
  setUserId,
  fetchUserData,
  setUserData,
  updateProfileInStore,
} from "../../redux/userSlice";
import { registerUser } from "../../services/authService";
import { createDogProfile, updateDogProfile } from "../../services/dogService";
import { updateUser, updateFcmToken } from "../../services/userService";
import "./OnboardingFinalPage.css";
import nextIcon from "../../assets/nextButtonIcon2.png";

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Bone, Heart } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useLoadScript } from "@react-google-maps/api";
import home from "../../assets/home.svg";
import gender from "../../assets/gender.svg";
import cake from "../../assets/cake.svg";
import pawIcon2 from "../../assets/pawIcon2_1.svg";
import greenPawIcon from "../../assets/greenPawIcon.svg";
import pawIcon3 from "../../assets/pawIcon3.svg";
import d2d from "../../assets/greenIconExpWithDogs.svg";
import d2h from "../../assets/greenIconExpWithHumans.svg";
import profileAvailability from "../../assets/greenCalendar.svg";
import { useGlobalAlert } from "../../context/AlertContext";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import MobileTopBar from "../../components/MobileTopBar";
import DesktopTopBar from "../../components/DesktopTopBar";

const parseAddressComponents = (components, formattedAddress = "") => {
  let streetNum = "",
    route = "",
    city = "",
    state = "",
    zip = "",
    country = "",
    neighborhood = "";

  components.forEach((component) => {
    const types = component.types;
    if (types.includes("neighborhood")) neighborhood = component.long_name;
    if (types.includes("street_number")) streetNum = component.long_name;
    if (types.includes("route")) route = component.long_name;
    if (
      types.includes("locality") ||
      types.includes("sublocality") ||
      types.includes("administrative_area_level_2")
    ) {
      if (!city) city = component.long_name;
    }
    if (types.includes("administrative_area_level_1"))
      state = component.short_name;
    if (types.includes("postal_code")) zip = component.long_name;
    if (types.includes("country")) country = component.long_name;
  });

  let street = `${streetNum} ${route}`.trim();
  if (!street && formattedAddress) {
    street = formattedAddress.split(",")[0].trim();
  }

  neighborhood = neighborhood || city;

  return { street, city, state, zip, country, neighborhood };
};

const OnboardingFinalPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerAlert } = useGlobalAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState("dog");

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const token = useSelector((state) => state.user.token);
  const profile = useSelector((state) => state.user.profile);
  const isSocialSignup = profile?.is_social_signup === 1;
  const isHalfCompletedFlow =
    formData?.onboardingMode === "update" ||
    formData?.dogPersonalityOrigin === "half-completed";
  const fcmToken = useSelector((state) => state.user.fcmToken);

  const toTitleCase = (value) => {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return null;
    }
    if (typeof value !== "string") return value;
    return value
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getFirstName = (fullName) => {
    if (!fullName) return "";
    return fullName.trim().split(" ")[0];
  };

  const isSocial = formData.team === "social";

  const handleBack = () => {
    if (isSocial) {
      navigate("/team-social-checklist", { state: { step: 2 } });
    } else {
      navigate("/user-details-step-two", { state: { step: 2 } });
    }
  };

  const displayData = useMemo(() => {
    const hasDogsArray = formData.dogs && formData.dogs.length > 0;
    const primaryDog =
      !isSocial && !formData.dogName && hasDogsArray
        ? formData.dogs[0]
        : formData;

    const backendDog =
      profile?.dogs && profile.dogs.length > 0 ? profile.dogs[0] : null;

    const extractImages = (newPhotosObj, existingUrlsArray) => {
      const result = [];

      const getImg = (key, index) => {
        const photo = newPhotosObj?.[key];

        if (photo) {
          if (photo instanceof File || photo instanceof Blob) {
            return URL.createObjectURL(photo);
          }
          if (typeof photo === "string") {
            return photo;
          }
        }

        if (existingUrlsArray?.[index]) return existingUrlsArray[index];
        return null;
      };

      const main = getImg("main", 0);
      const sub1 = getImg("sub1", 1);
      const sub2 = getImg("sub2", 2);
      const sub3 = getImg("sub3", 3);

      if (main) result.push(main);
      if (sub1) result.push(sub1);
      if (sub2) result.push(sub2);
      if (sub3) result.push(sub3);

      return result;
    };

    const userImages = extractImages(
      formData.photos,
      profile?.user_profile_image,
    );

    const images = isSocial
      ? userImages
      : extractImages(primaryDog.dogPhotos, backendDog?.dog_images);

    const joinIfArray = (value) => {
      if (!value) return null;

      const filterGarbage = (arr) => {
        return arr.filter(
          (item) =>
            typeof item === "string" &&
            item.trim() !== "" &&
            item.trim() !== "[" &&
            item.trim() !== "]",
        );
      };

      if (Array.isArray(value)) {
        const cleaned = filterGarbage(value);
        return cleaned.length ? cleaned.join(", ") : null;
      }

      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            const cleaned = filterGarbage(parsed);
            return cleaned.length ? cleaned.join(", ") : null;
          }
          return value.trim() === "" ? null : value;
        } catch {
          let cleanStr = value.replace(/\[|\]/g, "").trim();
          if (cleanStr.startsWith(",")) {
            cleanStr = cleanStr.substring(1).trim();
          }
          return cleanStr === "" ? null : cleanStr;
        }
      }
      return value;
    };

    const getPersonalityArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === "object") return Object.values(val);
      if (typeof val === "string") {
        try {
          const parsed = JSON.parse(val);
          if (Array.isArray(parsed)) return parsed;
          if (typeof parsed === "object") return Object.values(parsed);
        } catch {
          return val
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
        }
      }
      return [];
    };

    if (isSocial) {
      const availSocial = joinIfArray(formData.availability);

      return {
        name: toTitleCase(formData.name || "Pup Pal"),
        brings_me_here: formData?.selectedBringsYouHere
          ? toTitleCase(joinIfArray(formData?.selectedBringsYouHere))
          : null,
        title_icon: <Heart size={20} fill="#fff" stroke="none" />,
        vitals: [
          {
            label: "Bio",
            value: "Pup Pal",
            icon: <img src={gender} alt="gender" />,
          },
          {
            label: "Goal",
            value: "Social Connection",
            icon: <img src={pawIcon3} alt="pawIcon3" />,
          },
          {
            label: "Activity",
            value: toTitleCase(formData.neighborhood || formData.city),
            icon: <img src={home} alt="home" />,
          },
        ].filter((v) => v.value),
        attributes: [
          {
            label: "Bio",
            value: formData.userBio ? <b>{formData.userBio}</b> : null,
            icon: pawIcon2,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Activity",
            value: formData.dogActivity ? (
              <>
                I want to spend time with a pup for{" "}
                <b style={{ fontWeight: "700" }}>
                  {(() => {
                    const activity = formData.dogActivity?.toLowerCase();
                    if (activity === "both") {
                      return "Companionship and Experience";
                    }
                    if (activity === "companionship") {
                      return "Companionship";
                    }
                    if (activity === "experience") {
                      return "Experience";
                    }
                    return toTitleCase(formData.dogActivity);
                  })()}
                </b>
              </>
            ) : null,
            icon: pawIcon2,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Availability",
            value: availSocial ? (
              <>
                I'm generally{" "}
                <b style={{ fontWeight: "700" }}>
                  available in {toTitleCase(availSocial)} for walks or playdates
                </b>
              </>
            ) : null,
            icon: profileAvailability,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Experience",
            value: formData.pastExperienceLevelWithDogs?.label ? (
              <>
                In my past experience{" "}
                <b style={{ fontWeight: "700" }}>
                  {formData.pastExperienceLevelWithDogs.label}
                </b>
              </>
            ) : null,
            icon: d2h,
            width: window.innerWidth <= 1023 ? "18px" : "26px",
          },
        ].filter((attr) => attr.value !== null),
        images: images.length > 0 ? images : [userPlaceholder],
        tagIcon: (
          <img
            src={pawIcon2}
            alt="pawIcon2"
            style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
          />
        ),
        personalityList: getPersonalityArray(formData.pawsonalityAnswers)
          .map(toTitleCase)
          .filter(Boolean),
        hobbiesList: getPersonalityArray(formData.selectedHobbies)
          .map(toTitleCase)
          .filter(Boolean),
      };
    } else {
      let displayAge = null;
      if (primaryDog.dogAgeGroup?.trim()) {
        displayAge = primaryDog.dogAgeGroup.split("/")[0].trim();
      } else if (primaryDog.dogBirthDate?.trim()) {
        const birthDate = new Date(primaryDog.dogBirthDate);
        const diffDays = (new Date() - birthDate) / (1000 * 60 * 60 * 24);
        const years = Math.floor(diffDays / 365.25);
        const months = Math.floor(diffDays / 30.44);
        displayAge =
          years >= 1
            ? `${years} ${years === 1 ? "Year" : "Years"} Old`
            : `${months} ${months === 1 ? "Month" : "Months"} Old`;
      }

      let genderStr = "";
      if (primaryDog.dogGender) {
        const g = primaryDog.dogGender.toLowerCase();
        genderStr =
          g === "male" || g === "boy"
            ? "Boy"
            : g === "female" || g === "girl"
              ? "Girl"
              : toTitleCase(primaryDog.dogGender);
      }

      let displayBreed = null;
      if (primaryDog.dogBreed) {
        const mixedBreedsArray = [
          primaryDog.selectedBreed1,
          primaryDog.selectedBreed2,
          primaryDog.selectedBreed3,
        ].filter(Boolean);

        if (
          primaryDog.dogBreed.toLowerCase() === "mixed breed" &&
          mixedBreedsArray.length > 0
        ) {
          displayBreed = toTitleCase(
            `${primaryDog.dogBreed}, ${mixedBreedsArray.join(", ")}`,
          );
        } else {
          displayBreed = toTitleCase(primaryDog.dogBreed);
        }
      }

      const genderAndBreed = [genderStr, displayBreed]
        .filter(Boolean)
        .join(" ");

      let activityText = null;
      if (primaryDog.dogActivity) {
        const act = primaryDog.dogActivity.toLowerCase();
        if (act === "both") activityText = "Companionship and Exercise";
        else if (act === "companionship") activityText = "Companionship";
        else if (act === "exercise") activityText = "Exercise";
        else activityText = toTitleCase(primaryDog.dogActivity);
      }

      const availStr = joinIfArray(primaryDog.availability);
      const availabilityText = availStr
        ? `available in ${availStr} for walks or playdates`
        : null;

      const dName = primaryDog.dogName || "Your Pup";
      const uName = formData.name || "User";

      const dogAttributes = [
        {
          label: "Bio",
          value: primaryDog?.dogBio || null,
          icon: greenPawIcon,
          width: window.innerWidth <= 1023 ? "24px" : "28px",
        },
        {
          label: "Experience with other dogs",
          value: toTitleCase(
            primaryDog?.socialExperienceLevelWithDogs?.label ||
              formData?.socialExperienceLevelWithDogs?.label ||
              null,
          ),
          icon: d2d,
          width: window.innerWidth <= 1023 ? "26px" : "30px",
        },
        {
          label: "Experience with humans",
          value: toTitleCase(
            primaryDog?.socialExperienceWithHumans?.label ||
              formData?.socialExperienceWithHumans?.label ||
              null,
          ),
          icon: d2h,
          width: window.innerWidth <= 1023 ? "22px" : "26px",
        },
        {
          label: "Availability",
          value: toTitleCase(availabilityText),
          icon: profileAvailability,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
      ].filter((attr) => attr.value !== null);

      const bringsYouHereFormatted = toTitleCase(
        joinIfArray(formData?.selectedBringsYouHere),
      );

      const userAttributes = [
        {
          label: "Brings Me Here",
          value: bringsYouHereFormatted ? (
            <>
              <b style={{ fontWeight: "700" }}>I'm here because </b>
              {bringsYouHereFormatted}
            </>
          ) : null,
          icon: pawIcon2,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
        {
          label: "Bio",
          value: formData.userBio ? formData.userBio : null,
          icon: pawIcon2,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
      ].filter((attr) => attr.value !== null);

      if (activeView === "user") {
        return {
          name: toTitleCase(dName),
          userName: toTitleCase(uName),
          title_icon: <Heart size={20} fill="#fff" stroke="none" />,
          vitals: [],
          attributes: userAttributes,
          images: userImages.length > 0 ? userImages : [userPlaceholder],
          tagIcon: (
            <img
              src={pawIcon2}
              alt="pawIcon2"
              style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
            />
          ),
          brings_me_here: formData?.selectedBringsYouHere
            ? toTitleCase(joinIfArray(formData?.selectedBringsYouHere))
            : null,
        };
      } else {
        return {
          name: toTitleCase(dName),
          userName: toTitleCase(uName),
          dogBreed: displayBreed,
          genderAndBreed: genderAndBreed ? genderAndBreed : null,
          activityText: activityText,
          availabilityText: availabilityText,
          traitsList: getPersonalityArray(primaryDog.selectedTraits)
            .map(toTitleCase)
            .filter(Boolean),
          hobbiesList: getPersonalityArray(primaryDog.selectedHobbies)
            .map(toTitleCase)
            .filter(Boolean),
          brings_me_here: formData?.selectedBringsYouHere
            ? toTitleCase(joinIfArray(formData?.selectedBringsYouHere))
            : null,
          title_icon: <Bone size={20} fill="#fff" stroke="none" />,
          vitals: [
            {
              label: "Age",
              value: toTitleCase(displayAge),
              icon: <img src={cake} alt="cake" />,
            },
            {
              label: "Gender",
              value: genderStr,
              icon: <img src={gender} alt="gender" />,
            },
            {
              label: "Breed",
              value: toTitleCase(primaryDog.dogBreed),
              icon: <img src={pawIcon3} alt="pawIcon3" />,
            },
            {
              label: "Activity",
              value: toTitleCase(formData.neighborhood || formData.city),
              icon: <img src={home} alt="home" />,
            },
          ].filter((v) => v.value),
          attributes: dogAttributes,
          images: images.length > 0 ? images : [dogPlaceholder],
          bio: primaryDog.dogBio ? primaryDog.dogBio : null,
          tagIcon: (
            <img
              src={pawIcon2}
              alt="pawIcon2"
              style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
            />
          ),
          personalityList: getPersonalityArray(primaryDog.dogPersonality)
            .map(toTitleCase)
            .filter(Boolean),
        };
      }
    }
  }, [formData, profile, isSocial, activeView]);

  const handleNext = async () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      const addressComponents = [
        formData.street,
        formData.apartment,
        formData.city,
        formData.state,
        formData.zip,
      ]
        .filter(Boolean)
        .join(" ");

      let fetchedLat = formData.user_latitude || "";
      let fetchedLng = formData.user_longitude || "";

      if (!fetchedLat && addressComponents.trim() !== "") {
        try {
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              addressComponents,
            )}&key=${apiKey}`,
          );
          const data = await response.json();
          if (data.status === "OK" && data.results.length > 0) {
            fetchedLat = data.results[0].geometry.location.lat.toString();
            fetchedLng = data.results[0].geometry.location.lng.toString();
          }
        } catch (geoError) {
          console.error("Geocoding failed to fetch coordinates:", geoError);
        }
      }

      const oldUserToken =
        sessionStorage.getItem("temp_old_user_token") || token;
      const oldUserDataStr =
        sessionStorage.getItem("temp_old_user_data") || JSON.stringify(profile);

      const submitData = {
        ...formData,
        user_latitude: fetchedLat,
        user_longitude: fetchedLng,
        neighborhood: formData.neighborhood || formData.city,
      };

      if (formData.team === "paws") {
        submitData.user_dog_status = true;
        submitData.team_paws = true;
        submitData.team_social = false;
        submitData.isDogProfileHalfComplete = false;
      }
      submitData.isDogProfileComplete = true;

      if (isHalfCompletedFlow || (oldUserToken && oldUserDataStr)) {
        const parsedOldData = oldUserDataStr
          ? JSON.parse(oldUserDataStr)
          : profile || {};

        const userId =
          parsedOldData.id ||
          parsedOldData.user?.id ||
          parsedOldData.data?.user?.id ||
          profile?.id;

        const isOldUser =
          parsedOldData.is_old_user === 1 ||
          parsedOldData.is_old_user === true ||
          profile?.is_old_user === 1 ||
          profile?.is_old_user === true ||
          formData?.isOldUser === 1 ||
          formData?.isOldUser === true ||
          submitData.isOldUser === 1 ||
          submitData.isOldUser === true;

        // format date as YYYY-MM-DD HH:mm:ss
        const getFormattedDate = () => {
          const now = new Date();
          const pad = (n) => String(n).padStart(2, "0");
          return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
            now.getDate(),
          )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
            now.getSeconds(),
          )}`;
        };

        const mergedData = {
          ...submitData,
          name:
            submitData.name ||
            parsedOldData.user_name ||
            parsedOldData.user?.user_name,
          email:
            submitData.email ||
            parsedOldData.user_email ||
            parsedOldData.user?.user_email,
          street:
            submitData.street ||
            parsedOldData.user_address ||
            parsedOldData.user?.user_address,
          state:
            submitData.state ||
            parsedOldData.user_state ||
            parsedOldData.user?.user_state,
          zip:
            submitData.zip ||
            parsedOldData.user_zipcode ||
            parsedOldData.user?.user_zipcode,
          city:
            submitData.city ||
            parsedOldData.user_city ||
            parsedOldData.user?.user_city,
          neighborhood:
            submitData.neighborhood ||
            parsedOldData.neighborhood ||
            submitData.city ||
            parsedOldData.user_city,
          apartment: submitData.apartment || parsedOldData.user_address_line_2,
          phone: submitData.phone || parsedOldData.user_phone,
          userBio: submitData.userBio || parsedOldData.user_bio,
          user_latitude:
            submitData.user_latitude || parsedOldData.user_latitude,
          user_longitude:
            submitData.user_longitude || parsedOldData.user_longitude,
          team:
            submitData.team ||
            (parsedOldData.user_dog_status === 1 ? "paws" : "social"),
          selectedBringsYouHere:
            submitData.selectedBringsYouHere || parsedOldData.whats_brings_you,
          ...(isOldUser && { createdAt: getFormattedDate() }),
        };

        const updateResponse = await updateUser(
          userId,
          mergedData,
          fcmToken,
          oldUserToken,
        );

        if (fcmToken) {
          try {
            await updateFcmToken(userId, fcmToken);
          } catch (fcmError) {
            console.error("Failed to update FCM token:", fcmError);
          }
        }

        const isOldUserWithDog =
          parsedOldData.user_dog_status === 1 ||
          parsedOldData.user_dog_status === true ||
          profile?.user_dog_status === 1 ||
          profile?.user_dog_status === true ||
          formData?.userDogStatus === 1 ||
          formData?.userDogStatus === true ||
          mergedData.userDogStatus === 1 ||
          mergedData.userDogStatus === true ||
          mergedData.team === "paws";

        const dogsList =
          parsedOldData.dogs || parsedOldData.data?.dogs || profile?.dogs || [];
        const existingDogIds = dogsList.map((d) => d.id).filter(Boolean);
        const primaryDogId =
          existingDogIds[0] ||
          formData.dogId ||
          profile?.dogs?.[0]?.id ||
          formData.dogs?.[0]?.id;

        if (isOldUser) {
          if (primaryDogId) {
            let primaryDogData = { ...mergedData };
            if (mergedData.dogs?.length > 0) {
              const editedEntry =
                mergedData.dogs.find(
                  (d) => d.id === primaryDogId || d.dogName || d.dogActivity,
                ) || mergedData.dogs[0];
              primaryDogData = {
                ...editedEntry,
                ...mergedData,
                id: primaryDogId,
              };
            }

            await updateDogProfile(
              primaryDogId,
              userId,
              primaryDogData,
              mergedData,
            );
          }
        } else {
          if (primaryDogId) {
            let primaryDogData = { ...mergedData };
            if (mergedData.dogs?.length > 0) {
              const editedEntry =
                mergedData.dogs.find((d) => d.dogName || d.dogActivity) ||
                mergedData.dogs[mergedData.dogs.length - 1];
              primaryDogData = {
                ...editedEntry,
                ...mergedData,
                id: primaryDogId,
              };
            }

            await updateDogProfile(
              primaryDogId,
              userId,
              primaryDogData,
              mergedData,
            );

            if (mergedData.dogs?.length > 1) {
              const extraDogs = mergedData.dogs.filter(
                (d, idx) =>
                  d.id !== primaryDogId && idx > 0 && (d.dogName || d.dog_name),
              );
              for (const extraDog of extraDogs) {
                if (extraDog.id) {
                  await updateDogProfile(
                    extraDog.id,
                    userId,
                    extraDog,
                    mergedData,
                  );
                } else {
                  await createDogProfile(
                    userId,
                    extraDog,
                    mergedData,
                    oldUserToken,
                  );
                }
              }
            }
          } else {
            if (mergedData.dogs?.length > 0) {
              const dogPromises = mergedData.dogs.map((dogData) =>
                createDogProfile(userId, dogData, mergedData, oldUserToken),
              );
              await Promise.all(dogPromises);
            } else if (mergedData.dogName || mergedData.team === "social") {
              await createDogProfile(
                userId,
                mergedData,
                mergedData,
                oldUserToken,
              );
            }
          }
        }

        const latestProfileData = await dispatch(
          fetchUserData(userId),
        ).unwrap();
        dispatch(
          setUserData({
            token: oldUserToken,
            data: latestProfileData,
          }),
        );
        dispatch(updateProfileInStore(latestProfileData));
        dispatch(setUserId(userId));

        sessionStorage.removeItem("temp_old_user_token");
        sessionStorage.removeItem("temp_old_user_data");

        setFormData({});
        localStorage.setItem("dashboard_previous_view", "map");
        localStorage.setItem("dashboard_current_view", "map");
        const returnState = takeCompletionReturn(userId);
        navigate(returnState ? "/profile-details" : "/dashboard-switcher", {
          replace: true,
          state: returnState,
        });
      } else {
        const regResponse = await registerUser(submitData, fcmToken);

        if (regResponse.status && regResponse.data) {
          const newUserId = regResponse.data;

          if (fcmToken) {
            try {
              await updateFcmToken(newUserId, fcmToken);
            } catch (fcmError) {
              console.error("Failed to update FCM token:", fcmError);
            }
          }

          if (submitData.dogs && submitData.dogs.length > 0) {
            const dogPromises = submitData.dogs.map((dogData) =>
              createDogProfile(newUserId, dogData, submitData),
            );
            await Promise.all(dogPromises);
          } else if (submitData.team === "social") {
            await createDogProfile(newUserId, submitData, submitData);
          }

          dispatch(setUserId(newUserId));
          const latestProfileData = await dispatch(
            fetchUserData(newUserId),
          ).unwrap();
          dispatch(
            setUserData({ token: regResponse.token, data: latestProfileData }),
          );
          dispatch(updateProfileInStore(latestProfileData));
          setFormData({});

          localStorage.setItem("dashboard_previous_view", "map");
          localStorage.setItem("dashboard_current_view", "map");

          navigate("/dashboard-switcher");
        }
      }
    } catch (error) {
      console.error("Onboarding failed:", error);
      triggerAlert(error.message || "Something went wrong.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImgError = (e) => {
    e.target.src = dogPlaceholder;
  };

  const rightPanelRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const rightPanel = rightPanelRef.current;
    if (!rightPanel) return;

    function onScroll() {
      const { scrollTop, scrollHeight, clientHeight } = rightPanelRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setIsAtBottom(true);
      } else {
        setIsAtBottom(false);
      }
    }

    rightPanel.addEventListener("scroll", onScroll);
    return () => rightPanel.removeEventListener("scroll", onScroll);
  }, []);

  const feedImages = displayData?.images?.slice(1) || [];

  const hasRightPanelData = useMemo(() => {
    if (isSocial) {
      return (
        feedImages.length > 0 ||
        (displayData.personalityList &&
          displayData.personalityList.length > 0) ||
        (displayData.hobbiesList && displayData.hobbiesList.length > 0) ||
        !!displayData.brings_me_here
      );
    } else if (activeView === "user") {
      return feedImages.length > 0;
    } else {
      return (
        feedImages.length > 0 ||
        (displayData.traitsList && displayData.traitsList.length > 0) ||
        (displayData.hobbiesList && displayData.hobbiesList.length > 0) ||
        !!displayData.activityText ||
        (displayData.personalityList && displayData.personalityList.length > 0)
      );
    }
  }, [isSocial, activeView, feedImages, displayData]);

  return (
    <div className="ofp-container">
      <MobileTopBar
        onBack={handleBack}
        backColor="#000"
        logoColor="#009fd9"
        blueLogo="true"
        isProtectedRoute={false}
      />
      <DesktopTopBar
        showBack={true}
        onBack={handleBack}
        backColor="#000"
        showProgress={false}
      />

      <div className="ofp-content">
        <h1 className="ofp-title">Profile preview</h1>

        <div
          className="ofp-summary-grid"
          style={
            !hasRightPanelData
              ? { display: "flex", justifyContent: "center" }
              : {}
          }
        >
          <div className="ofp-detail-left-panel">
            <div className="ofp-left-panel-wrapper">
              {!isSocial && (
                <div className="ofp-parent-names-header">
                  <span
                    onClick={() => setActiveView("dog")}
                    style={{
                      cursor: "pointer",
                      opacity: activeView === "dog" ? 1 : 0.7,
                      fontWeight: activeView === "dog" ? "bold" : "normal",
                    }}
                  >
                    <span className="ofp-desktop-inline">
                      {displayData?.name}
                    </span>
                    <span className="ofp-mobile-inline">
                      {getFirstName(displayData?.name)}
                    </span>
                  </span>
                  <span className="ofp-name-separator"></span>
                  <span
                    onClick={() => setActiveView("user")}
                    style={{
                      cursor: "pointer",
                      opacity: activeView === "user" ? 1 : 0.7,
                      fontWeight: activeView === "user" ? "bold" : "normal",
                    }}
                  >
                    <span className="ofp-desktop-inline">
                      {displayData?.userName}
                    </span>
                    <span className="ofp-mobile-inline">
                      {getFirstName(displayData?.userName)}
                    </span>
                  </span>
                </div>
              )}

              <div className="ofp-profile-summary-card">
                <div className="ofp-main-image-container">
                  <div className="ofp-image-relative-wrapper">
                    <img
                      src={
                        displayData.images[0] ||
                        (isSocial || activeView === "user"
                          ? userPlaceholder
                          : dogPlaceholder)
                      }
                      alt="Avatar"
                      onError={handleImgError}
                      className="ofp-main-dog-image"
                    />
                  </div>
                  <div className="ofp-image-overlay-text">
                    {displayData.title_icon}
                    <span className="ofp-dog-name-overlay">
                      {activeView === "user" ? (
                        <>
                          <span className="ofp-desktop-inline">
                            {displayData.userName}
                          </span>
                          <span className="ofp-mobile-inline">
                            {getFirstName(displayData.userName)}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="ofp-desktop-inline">
                            {displayData.name || displayData.userName}
                          </span>
                          <span className="ofp-mobile-inline">
                            {getFirstName(
                              displayData.name || displayData.userName,
                            )}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {displayData.vitals &&
                  displayData.vitals.length > 0 &&
                  !isSocial && (
                    <div className="ofp-vitals-row">
                      {displayData.vitals.map((vital, i) => (
                        <div key={i} className="ofp-vital-item">
                          {React.cloneElement(vital.icon, {
                            style: { stroke: "rgba(68, 138, 123, 1)" },
                            strokeWidth: 3,
                            color: "transparent",
                            width: "22px",
                          })}{" "}
                          {vital.value}
                        </div>
                      ))}
                    </div>
                  )}

                <div className="ofp-profile-attributes">
                  {displayData.attributes.map((attr, i) => (
                    <div key={i} className="ofp-attr-row">
                      <div className="ofp-attr-icon-wrapper">
                        <img
                          src={attr.icon}
                          alt={attr.label}
                          style={{
                            width: attr.width || "32px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      <span className="ofp-attr-text">{attr.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {hasRightPanelData && (
            <div
              className="ofp-detail-right-panel"
              ref={rightPanelRef}
              style={{
                overflowY: "auto",
                height: "100%",
                padding: "10px 30px",
              }}
            >
              <div className="ofp-feed-container">
                <div
                  className="ofp-bio-wrapper"
                  style={{ marginTop: "0rem" }}
                ></div>

                {isSocial ? (
                  <>
                    {feedImages.length > 0 && (
                      <div className="ofp-feed-card ofp-photo-card">
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "330px",
                            margin: "0 auto",
                          }}
                        >
                          <img
                            src={feedImages[0] || userPlaceholder}
                            alt={`${displayData.name} feed 1`}
                            onError={handleImgError}
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: 16,
                              objectFit: "cover",
                            }}
                          />
                          <div className="ofp-feed-gradient-top" />
                          <div className="ofp-feed-gradient-bottom" />
                        </div>
                      </div>
                    )}

                    {displayData.personalityList &&
                      displayData.personalityList.length > 0 && (
                        <div
                          className="ofp-bio-wrapper"
                          style={{ marginTop: "1rem" }}
                        >
                          <span className="ofp-tag-icon">
                            {displayData.tagIcon}
                          </span>
                          <div className="ofp-feed-card ofp-text-card">
                            {displayData.personalityList.map((trait, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom:
                                    index ===
                                    displayData.personalityList.length - 1
                                      ? 0
                                      : "8px",
                                }}
                              >
                                <p
                                  className="ofp-bio-text"
                                  style={{ margin: 0 }}
                                >
                                  <b style={{ fontWeight: "700" }}>{trait}</b>
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {feedImages.length > 1 && (
                      <div className="ofp-feed-card ofp-photo-card">
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "330px",
                            margin: "0 auto",
                          }}
                        >
                          <img
                            src={feedImages[1] || userPlaceholder}
                            alt={`${displayData.name} feed 2`}
                            onError={handleImgError}
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: 16,
                              objectFit: "cover",
                            }}
                          />
                          <div className="ofp-feed-gradient-top" />
                          <div className="ofp-feed-gradient-bottom" />
                        </div>
                      </div>
                    )}

                    {displayData.brings_me_here && (
                      <div
                        className="ofp-bio-wrapper"
                        style={{ marginTop: "1rem" }}
                      >
                        <span className="ofp-tag-icon">
                          {displayData.tagIcon}
                        </span>
                        <div className="ofp-feed-card ofp-text-card">
                          <p className="ofp-bio-text">
                            I'm here because{" "}
                            <b style={{ fontWeight: "700" }}>
                              {displayData.brings_me_here}
                            </b>
                          </p>
                        </div>
                      </div>
                    )}

                    {feedImages.slice(2).map((imgUrl, index) => (
                      <React.Fragment key={index}>
                        <div className="ofp-feed-card ofp-photo-card">
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              maxWidth: "330px",
                              margin: "0 auto",
                            }}
                          >
                            <img
                              src={imgUrl || userPlaceholder}
                              alt={`${displayData.name} feed ${index + 3}`}
                              onError={handleImgError}
                              style={{
                                display: "block",
                                width: "100%",
                                aspectRatio: "1 / 1",
                                borderRadius: 16,
                                objectFit: "cover",
                              }}
                            />
                            <div className="ofp-feed-gradient-top" />
                            <div className="ofp-feed-gradient-bottom" />
                          </div>
                        </div>
                      </React.Fragment>
                    ))}

                    {displayData.hobbiesList &&
                      displayData.hobbiesList.length > 0 && (
                        <div
                          className="ofp-bio-wrapper"
                          style={{ marginTop: "1rem" }}
                        >
                          <span className="ofp-tag-icon">
                            {displayData.tagIcon}
                          </span>
                          <div className="ofp-feed-card ofp-text-card">
                            <p className="ofp-bio-text" style={{ margin: 0 }}>
                              <b style={{ fontWeight: "700" }}>
                                {/* {displayData.name} enjoys{" "} */}
                                {displayData.hobbiesList.join(", ")}
                              </b>
                            </p>
                          </div>
                        </div>
                      )}
                  </>
                ) : activeView === "user" ? (
                  <>
                    {feedImages.map((imgUrl, index) => (
                      <div
                        className="ofp-feed-card ofp-photo-card"
                        key={`user-feed-${index}`}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "330px",
                            margin: "0 auto",
                          }}
                        >
                          <img
                            src={imgUrl || userPlaceholder}
                            alt={`${displayData.userName} feed ${index + 1}`}
                            onError={handleImgError}
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: 16,
                              objectFit: "cover",
                            }}
                          />
                          <div className="ofp-feed-gradient-top" />
                          <div className="ofp-feed-gradient-bottom" />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {feedImages.length > 0 && (
                      <div className="ofp-feed-card ofp-photo-card">
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "330px",
                            margin: "0 auto",
                          }}
                        >
                          <img
                            src={feedImages[0] || dogPlaceholder}
                            alt={`${displayData.name} feed 1`}
                            onError={handleImgError}
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: 16,
                              objectFit: "cover",
                            }}
                          />
                          <div className="ofp-feed-gradient-top" />
                          <div className="ofp-feed-gradient-bottom" />
                        </div>
                      </div>
                    )}

                    {displayData.traitsList &&
                      displayData.traitsList.length > 0 && (
                        <div
                          className="ofp-bio-wrapper"
                          style={{ marginTop: "1rem" }}
                        >
                          <span className="ofp-tag-icon">
                            {displayData.tagIcon}
                          </span>
                          <div className="ofp-feed-card ofp-text-card">
                            <p className="ofp-bio-text" style={{ margin: 0 }}>
                              <b style={{ fontWeight: "700" }}>
                                {displayData.name} is a{" "}
                                {displayData.traitsList.join(", ")}
                              </b>
                            </p>
                          </div>
                        </div>
                      )}

                    {feedImages.length > 1 && (
                      <div className="ofp-feed-card ofp-photo-card">
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "330px",
                            margin: "0 auto",
                          }}
                        >
                          <img
                            src={feedImages[1] || dogPlaceholder}
                            alt={`${displayData.name} feed 2`}
                            onError={handleImgError}
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: 16,
                              objectFit: "cover",
                            }}
                          />
                          <div className="ofp-feed-gradient-top" />
                          <div className="ofp-feed-gradient-bottom" />
                        </div>
                      </div>
                    )}

                    {displayData.hobbiesList &&
                      displayData.hobbiesList.length > 0 && (
                        <div
                          className="ofp-bio-wrapper"
                          style={{ marginTop: "1rem" }}
                        >
                          <span className="ofp-tag-icon">
                            {displayData.tagIcon}
                          </span>
                          <div className="ofp-feed-card ofp-text-card">
                            <p className="ofp-bio-text" style={{ margin: 0 }}>
                              <b style={{ fontWeight: "700" }}>
                                {displayData.name} enjoys{" "}
                                {displayData.hobbiesList.join(", ")}
                              </b>
                            </p>
                          </div>
                        </div>
                      )}

                    {feedImages.length > 2 && (
                      <div className="ofp-feed-card ofp-photo-card">
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "330px",
                            margin: "0 auto",
                          }}
                        >
                          <img
                            src={feedImages[2] || dogPlaceholder}
                            alt={`${displayData.name} feed 3`}
                            onError={handleImgError}
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: 16,
                              objectFit: "cover",
                            }}
                          />
                          <div className="ofp-feed-gradient-top" />
                          <div className="ofp-feed-gradient-bottom" />
                        </div>
                      </div>
                    )}

                    {displayData.activityText && (
                      <div
                        className="ofp-bio-wrapper"
                        style={{ marginTop: "1rem" }}
                      >
                        <span className="ofp-tag-icon">
                          {displayData.tagIcon}
                        </span>
                        <div className="ofp-feed-card ofp-text-card">
                          <p className="ofp-bio-text">
                            {displayData.name} is here for{" "}
                            <b style={{ fontWeight: "700" }}>
                              {displayData.activityText}
                            </b>
                          </p>
                        </div>
                      </div>
                    )}

                    {displayData.personalityList &&
                      displayData.personalityList.length > 0 && (
                        <div
                          className="ofp-bio-wrapper"
                          style={{ marginTop: "1rem" }}
                        >
                          <span className="ofp-tag-icon">
                            {displayData.tagIcon}
                          </span>
                          <div className="ofp-feed-card ofp-text-card">
                            {displayData.personalityList.map((trait, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom:
                                    index ===
                                    displayData.personalityList.length - 1
                                      ? 0
                                      : "8px",
                                }}
                              >
                                <p
                                  className="ofp-bio-text"
                                  style={{ margin: 0 }}
                                >
                                  <b style={{ fontWeight: "700" }}>{trait}</b>
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {feedImages.slice(3).map((imgUrl, index) => (
                      <React.Fragment key={index}>
                        <div className="ofp-feed-card ofp-photo-card">
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              maxWidth: "330px",
                              margin: "0 auto",
                            }}
                          >
                            <img
                              src={imgUrl || dogPlaceholder}
                              alt={`${displayData.name} feed ${index + 4}`}
                              onError={handleImgError}
                              style={{
                                display: "block",
                                width: "100%",
                                aspectRatio: "1 / 1",
                                borderRadius: 16,
                                objectFit: "cover",
                              }}
                            />
                            <div className="ofp-feed-gradient-top" />
                            <div className="ofp-feed-gradient-bottom" />
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </>
                )}

                <div
                  className="ofp-feed-card ofp-contact-card"
                  style={{ visibility: "hidden" }}
                >
                  <div className="ofp-contact-row ofp-top-row"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ofp-footer">
        <button
          onClick={handleNext}
          className="ofp-next-btn"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="ofp-spinner-container">
              <div className="ofp-simple-spinner"></div>
            </div>
          ) : (
            <>
              <span className="ofp-btn-text">START EXPLORING</span>
              <div className="ofp-btn-circle">
                <img src={nextIcon} alt="Next Icon" className="ofp-btn-icon" />
              </div>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OnboardingFinalPage;
